// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional permissions under AGPL-3.0 section 7 apply — see ADDITIONAL-TERMS.md.

/**
 * The arithmetic — weights, KV cache, offload split and the verdict.
 *
 * Every function here is pure and exhaustively unit-tested against real,
 * published llama.cpp numbers. Two invariants anchor the whole module:
 *
 *  1. `dataStart + Σ align(tensorBytes)` must equal the file's true byte size.
 *     Verified delta = 0 on real models spanning K-quants, IQ-quants and MXFP4.
 *     Anything else means a type we mis-sized, so it is surfaced, not hidden.
 *
 *  2. The KV cache reproduces llama.cpp's own allocation:
 *     `Σ_layers n_ctx × (head_k × n_head_kv × bytes_k + head_v × n_head_kv × bytes_v)`.
 *     Llama-3.1-8B at 131072 gives 16384.00 MiB, matching the figure in the
 *     model's own startup log.
 */

import type { GgufTensorInfo } from './gguf/types';
import type { Geometry } from './geometry';
import { padContext } from './geometry';
import { alignUp, bitsPerWeight, cacheBytesPerElement, lookupType, tensorBytes, type GgmlType } from './quant';

// ── Weights ──────────────────────────────────────────────────────────

export interface TypeTally {
  name: string;
  count: number;
  elements: number;
  bytes: number;
  bitsPerWeight: number;
}

export interface UnsizeableTensor {
  name: string;
  typeId: number;
  label: string;
  detail: string;
}

export interface WeightSummary {
  /** Total bytes of tensor data, each tensor padded to the file alignment. */
  totalBytes: number;
  params: number;
  /** Real bits per weight across the whole model. */
  bpw: number;
  /** Bytes belonging to each repeating block, indexed by layer. */
  layerBytes: number[];
  /** Bytes for everything that is not a numbered block. */
  nonLayerBytes: number;
  /** The token embedding table specifically — it stays on the host by default. */
  embeddingBytes: number;
  /** The output head + final norm — offloaded only when every layer is. */
  outputBytes: number;
  byType: TypeTally[];
  unsizeable: UnsizeableTensor[];
  /** dataStart + Σ aligned tensor bytes — what the file *should* measure. */
  expectedFileBytes: number;
}

/** `blk.12.attn_q.weight` → 12. Anything else is a global tensor. */
export function layerIndexOf(name: string): number | null {
  const m = /^blk\.(\d+)\./.exec(name);
  return m ? Number(m[1]) : null;
}

export function summariseWeights(
  tensors: GgufTensorInfo[],
  dataStart: number,
  alignment: number,
  nLayer: number,
): WeightSummary {
  const layerBytes = new Array<number>(nLayer).fill(0);
  const tallies = new Map<string, TypeTally>();
  const unsizeable: UnsizeableTensor[] = [];

  let totalBytes = 0;
  let params = 0;
  let nonLayerBytes = 0;
  let embeddingBytes = 0;
  let outputBytes = 0;

  for (const t of tensors) {
    const lookup = lookupType(t.typeId);
    if (!lookup.ok) {
      unsizeable.push({ name: t.name, typeId: t.typeId, label: lookup.label, detail: lookup.detail });
      continue;
    }
    const type: GgmlType = lookup.type;
    const raw = tensorBytes(type, t.elements);
    const padded = alignUp(raw, alignment);

    totalBytes += padded;
    params += t.elements;

    const layer = layerIndexOf(t.name);
    if (layer !== null && layer < nLayer) {
      layerBytes[layer] += padded;
    } else {
      nonLayerBytes += padded;
      if (/^token_embd\./.test(t.name)) embeddingBytes += padded;
      else outputBytes += padded;
    }

    const tally = tallies.get(type.name) ?? {
      name: type.name,
      count: 0,
      elements: 0,
      bytes: 0,
      bitsPerWeight: bitsPerWeight(type),
    };
    tally.count += 1;
    tally.elements += t.elements;
    tally.bytes += padded;
    tallies.set(type.name, tally);
  }

  const byType = [...tallies.values()].sort((a, b) => b.bytes - a.bytes);

  return {
    totalBytes,
    params,
    bpw: params > 0 ? (totalBytes * 8) / params : 0,
    layerBytes,
    nonLayerBytes,
    embeddingBytes,
    outputBytes,
    byType,
    unsizeable,
    expectedFileBytes: dataStart + totalBytes,
  };
}

// ── KV cache ─────────────────────────────────────────────────────────

export interface KvOptions {
  nCtx: number;
  cacheTypeK: string;
  cacheTypeV: string;
  /** llama.cpp's --parallel: the cache is split across this many sequences. */
  parallel?: number;
}

export interface KvSummary {
  /** Total bytes for all layers. */
  totalBytes: number;
  /** Bytes for one layer, indexed by layer (they can differ under hybrid GQA). */
  perLayerBytes: number[];
  keyBytes: number;
  valueBytes: number;
  /** The context actually allocated after llama.cpp's 256-token padding. */
  paddedCtx: number;
  bytesPerToken: number;
}

export function computeKvCache(geo: Geometry, opts: KvOptions): KvSummary {
  const paddedCtx = padContext(opts.nCtx);
  const bk = cacheBytesPerElement(opts.cacheTypeK);
  const bv = cacheBytesPerElement(opts.cacheTypeV);

  const perLayerBytes: number[] = [];
  let keyBytes = 0;
  let valueBytes = 0;

  for (let i = 0; i < geo.nLayer; i++) {
    const heads = geo.nHeadKv[i] ?? geo.nHeadKv[geo.nHeadKv.length - 1] ?? geo.nHead;
    const k = paddedCtx * geo.headK * heads * bk;
    const v = paddedCtx * geo.headV * heads * bv;
    keyBytes += k;
    valueBytes += v;
    perLayerBytes.push(k + v);
  }

  const streams = Math.max(1, opts.parallel ?? 1);
  const totalBytes = (keyBytes + valueBytes) * streams;

  return {
    totalBytes,
    perLayerBytes,
    keyBytes: keyBytes * streams,
    valueBytes: valueBytes * streams,
    paddedCtx,
    bytesPerToken: paddedCtx > 0 ? totalBytes / paddedCtx : 0,
  };
}

// ── Offload split ────────────────────────────────────────────────────

export interface OffloadPlan {
  /** Repeating blocks placed on the GPU. */
  layersOnGpu: number;
  /** True when the output head and final norm are also on the GPU. */
  outputOnGpu: boolean;
  weightBytesGpu: number;
  weightBytesHost: number;
  kvBytesGpu: number;
  kvBytesHost: number;
}

/**
 * Model llama.cpp's placement for a given `-ngl`.
 *
 * llama.cpp offloads the LAST `n_gpu_layers` repeating blocks
 * (`i_gpu_start = n_layer - n_gpu_layers`), and puts the output head and final
 * norm on the GPU only when `n_gpu_layers > n_layer` — which is what `-ngl 99`
 * and `-ngl all` are really for. The token embedding table is a lookup and
 * stays on the host. The KV cache follows its layer.
 */
export function planOffload(weights: WeightSummary, kv: KvSummary, nLayer: number, ngl: number): OffloadPlan {
  const layersOnGpu = Math.max(0, Math.min(ngl, nLayer));
  const outputOnGpu = ngl > nLayer;
  const firstGpuLayer = nLayer - layersOnGpu;

  let weightBytesGpu = 0;
  let kvBytesGpu = 0;
  for (let i = firstGpuLayer; i < nLayer; i++) {
    weightBytesGpu += weights.layerBytes[i] ?? 0;
    kvBytesGpu += kv.perLayerBytes[i] ?? 0;
  }
  if (outputOnGpu) weightBytesGpu += weights.outputBytes;

  return {
    layersOnGpu,
    outputOnGpu,
    weightBytesGpu,
    weightBytesHost: weights.totalBytes - weightBytesGpu,
    kvBytesGpu,
    kvBytesHost: kv.totalBytes - kvBytesGpu,
  };
}

// ── Verdict ──────────────────────────────────────────────────────────

/**
 * Working memory llama.cpp needs beyond weights and KV: the compute buffer for
 * intermediate activations, plus the backend's own context.
 *
 * There is deliberately no formula here. The compute buffer depends on batch
 * size, backend and whether flash attention is on, and no closed form for it
 * survives contact with real builds — so this is presented in the UI as an
 * adjustable *allowance*, shown as its own line, never folded silently into
 * the headline. 512 MiB is a defensible default for a single-GPU CUDA build at
 * the default batch sizes.
 */
export const DEFAULT_RESERVE_BYTES = 512 * 1024 * 1024;

export type Verdict = 'fits' | 'tight' | 'no';

export interface FitResult {
  verdict: Verdict;
  /** Everything that has to be in VRAM. */
  requiredBytes: number;
  weightBytesGpu: number;
  kvBytesGpu: number;
  reserveBytes: number;
  vramBytes: number;
  /** Positive when it fits, negative by the amount it overflows. */
  headroomBytes: number;
  headroomFraction: number;
  plan: OffloadPlan;
}

export function evaluateFit(
  weights: WeightSummary,
  kv: KvSummary,
  geo: Geometry,
  ngl: number,
  vramBytes: number,
  reserveBytes = DEFAULT_RESERVE_BYTES,
): FitResult {
  const plan = planOffload(weights, kv, geo.nLayer, ngl);
  // The reserve is only paid when something is actually on the GPU.
  const reserve = plan.layersOnGpu > 0 ? reserveBytes : 0;
  const requiredBytes = plan.weightBytesGpu + plan.kvBytesGpu + reserve;
  const headroomBytes = vramBytes - requiredBytes;
  const headroomFraction = vramBytes > 0 ? headroomBytes / vramBytes : 0;

  let verdict: Verdict;
  if (headroomBytes < 0) verdict = 'no';
  else if (headroomFraction < 0.04) verdict = 'tight';
  else verdict = 'fits';

  return {
    verdict,
    requiredBytes,
    weightBytesGpu: plan.weightBytesGpu,
    kvBytesGpu: plan.kvBytesGpu,
    reserveBytes: reserve,
    vramBytes,
    headroomBytes,
    headroomFraction,
    plan,
  };
}

// ── Solvers ──────────────────────────────────────────────────────────

/**
 * The largest `-ngl` that still fits. Returns `nLayer + 1` when everything
 * including the output head fits, which is the `-ngl all` case.
 */
export function solveMaxLayers(
  weights: WeightSummary,
  kv: KvSummary,
  geo: Geometry,
  vramBytes: number,
  reserveBytes = DEFAULT_RESERVE_BYTES,
): number {
  for (let ngl = geo.nLayer + 1; ngl >= 1; ngl--) {
    if (evaluateFit(weights, kv, geo, ngl, vramBytes, reserveBytes).headroomBytes >= 0) return ngl;
  }
  return 0;
}

/**
 * The largest context that still fits with everything offloaded, found by
 * binary search over the 256-token allocation granularity. Returns 0 when the
 * weights alone already overflow the card.
 */
export function solveMaxContext(
  weights: WeightSummary,
  geo: Geometry,
  cacheTypeK: string,
  cacheTypeV: string,
  vramBytes: number,
  reserveBytes = DEFAULT_RESERVE_BYTES,
  ceiling = 1 << 21,
): number {
  const ngl = geo.nLayer + 1;
  const fitsAt = (ctx: number) => {
    const kv = computeKvCache(geo, { nCtx: ctx, cacheTypeK, cacheTypeV });
    return evaluateFit(weights, kv, geo, ngl, vramBytes, reserveBytes).headroomBytes >= 0;
  };
  if (!fitsAt(256)) return 0;

  let lo = 256;
  let hi = Math.max(256, Math.min(ceiling, geo.ctxTrain ?? ceiling));
  if (fitsAt(hi)) return hi;
  while (hi - lo > 256) {
    const mid = Math.floor((lo + hi) / 2 / 256) * 256;
    if (mid <= lo) break;
    if (fitsAt(mid)) lo = mid;
    else hi = mid;
  }
  return lo;
}
